import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Instagram, Facebook, MapPin, Phone, Mail } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom icons for social media
const TikTokIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const SnapchatIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.074-.36-.075-.765-.135-1.273-.135-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.675-3.279-1.288-.599-.42-1.107-.779-1.707-.884-.314-.045-.629-.074-.928-.074-.54 0-.958.089-1.272.149-.211.043-.391.074-.54.074-.374 0-.523-.224-.583-.42-.061-.192-.09-.389-.135-.567-.046-.181-.105-.494-.166-.57-1.918-.222-2.95-.642-3.189-1.226-.031-.063-.052-.15-.055-.225-.015-.243.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809-.121-.029-.24-.074-.346-.119-1.107-.435-1.257-.93-1.197-1.273.09-.479.674-.793 1.168-.793.146 0 .27.029.383.074.42.194.789.3 1.104.3.234 0 .384-.06.465-.105l-.046-.569c-.098-1.626-.225-3.651.307-4.837C7.392 1.077 10.739.807 11.727.807l.419-.015h.06z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Helper to get correct image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/uploads/')) return `${API_URL}${url}`;
  if (url.startsWith('/uploads/')) return `${API_URL}/api${url}`;
  return url;
};

export const Footer = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings`);
        setSettings(res.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const logoUrl = settings?.logo_url ? getImageUrl(settings.logo_url) : '/logo.png';
  const socialLinks = settings?.social_links || {};

  return (
    <footer className="bg-brand-black text-brand-white" data-testid="footer">
      {/* Elegant gold line */}
      <div className="luxury-divider" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {/* Brand */}
          <div className="col-span-2 sm:col-span-2 lg:col-span-1 space-y-6">
            <Link to="/" className="inline-block">
              <img 
                src={logoUrl || "/logo.png"} 
                alt={settings?.store_name || 'ALINA ELEGANCE'} 
                className="h-20 sm:h-24 w-auto"
                onError={(e) => { e.target.src = '/logo.png'; }}
              />
            </Link>
            <p className="text-brand-white/50 text-sm leading-relaxed font-body">
              L'élégance commence ici. Découvrez notre collection exclusive de produits de beauté.
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              {socialLinks.instagram && (
                <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="text-brand-white/50 hover:text-brand-gold transition-colors">
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {socialLinks.facebook && (
                <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="text-brand-white/50 hover:text-brand-gold transition-colors">
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {socialLinks.tiktok && (
                <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="text-brand-white/50 hover:text-brand-gold transition-colors">
                  <TikTokIcon />
                </a>
              )}
              {socialLinks.snapchat && (
                <a href={socialLinks.snapchat} target="_blank" rel="noopener noreferrer" className="text-brand-white/50 hover:text-brand-gold transition-colors">
                  <SnapchatIcon />
                </a>
              )}
              {socialLinks.whatsapp && (
                <a href={socialLinks.whatsapp} target="_blank" rel="noopener noreferrer" className="text-brand-white/50 hover:text-brand-gold transition-colors">
                  <WhatsAppIcon />
                </a>
              )}
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-brand-gold font-heading text-lg mb-4 tracking-wide">{t('footer.shop')}</h3>
            <ul className="space-y-2">
              <li><Link to="/products" className="text-brand-white/50 hover:text-brand-gold text-sm transition-colors font-body">{t('nav.shop')}</Link></li>
              <li><Link to="/products?filter=new" className="text-brand-white/50 hover:text-brand-gold text-sm transition-colors font-body">{t('nav.newArrivals')}</Link></li>
              <li><Link to="/products?filter=bestseller" className="text-brand-white/50 hover:text-brand-gold text-sm transition-colors font-body">{t('nav.bestsellers')}</Link></li>
            </ul>
          </div>

          {/* Info */}
          <div>
            <h3 className="text-brand-gold font-heading text-lg mb-4 tracking-wide">{t('footer.help')}</h3>
            <ul className="space-y-2">
              <li><Link to="/faq" className="text-brand-white/50 hover:text-brand-gold text-sm transition-colors font-body">{t('footer.faq')}</Link></li>
              <li><Link to="/livraison" className="text-brand-white/50 hover:text-brand-gold text-sm transition-colors font-body">{t('footer.delivery')}</Link></li>
              <li><Link to="/conditions" className="text-brand-white/50 hover:text-brand-gold text-sm transition-colors font-body">{t('footer.terms')}</Link></li>
              <li><Link to="/contact" className="text-brand-white/50 hover:text-brand-gold text-sm transition-colors font-body">{t('nav.contact')}</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div className="col-span-2 sm:col-span-1">
            <h3 className="text-brand-gold font-heading text-lg mb-4 tracking-wide">{t('footer.contact')}</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3 text-brand-white/50 text-sm font-body">
                <MapPin className="h-4 w-4 text-brand-gold flex-shrink-0 mt-0.5" />
                <span>{settings?.pickup_address || '130 Topham Terrace, Orléans, ON'}</span>
              </li>
              <li className="flex items-center space-x-3 text-brand-white/50 text-sm font-body">
                <Phone className="h-4 w-4 text-brand-gold flex-shrink-0" />
                <span>{settings?.contact_phone || '+1(613)3270164'}</span>
              </li>
              <li className="flex items-center space-x-3 text-brand-white/50 text-sm font-body">
                <Mail className="h-4 w-4 text-brand-gold flex-shrink-0" />
                <span className="break-all">{settings?.contact_email || 'alinaelegance1@gmail.com'}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="luxury-divider mt-10" />
        <div className="pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-brand-white/30 text-sm text-center sm:text-left font-body">
            © {new Date().getFullYear()} {settings?.store_name || 'ALINA ELEGANCE'}. {t('footer.allRights')}
          </p>
          <div className="flex items-center gap-6 text-brand-white/30 text-sm font-body">
            <Link to="/conditions" className="hover:text-brand-gold transition-colors">{t('footer.terms')}</Link>
            <Link to="/faq" className="hover:text-brand-gold transition-colors">{t('footer.faq')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
